import type { ColumnVisibilityState } from '@tanstack/react-table';

import { DataTable } from '@/components/organisms/DataTable';
import type { AlertAcknowledgement, Alert as AlertEntry } from '@/domain/alerts';
import type { ActiveBioscope } from '@/domain/bioscopes';

import { DEFAULT_PAGE_SIZE } from '../bioscopeListColumns';
import { toBioscopeRows } from '../bioscopeRows';
import { useBioscopeTableColumns } from '../useBioscopeTableColumns';

interface BioscopeTableProps {
    bioscopes: ActiveBioscope[];
    alerts: AlertEntry[];
    acknowledgements: AlertAcknowledgement[];
    filter: string;
    onBioscopeUpdate?: () => void;
    columnVisibility?: ColumnVisibilityState;
    onColumnVisibilityChange?: (visibility: ColumnVisibilityState) => void;
    pageSize?: number;
    onPageSizeChange?: (pageSize: number) => void;
    /** The `fish-group-tracking` flag. Off, the table and its links are as before fish groups. */
    fishGroupsEnabled?: boolean;
}

export default function BioscopeTable({
    bioscopes,
    alerts,
    acknowledgements,
    filter,
    columnVisibility,
    onColumnVisibilityChange,
    pageSize,
    onPageSizeChange,
    fishGroupsEnabled = false,
}: BioscopeTableProps) {
    const data = toBioscopeRows(bioscopes, alerts, acknowledgements);
    const columns = useBioscopeTableColumns(acknowledgements, fishGroupsEnabled);

    return (
        <DataTable
            responsive
            columns={columns}
            data={data}
            filter={filter}
            initialSort={[{ id: 'lastMeasurement', desc: true }]}
            enablePagination={true}
            pageSize={pageSize ?? DEFAULT_PAGE_SIZE}
            pageSizeOptions={[15, 25, 50, 100, 1000]}
            onPageSizeChange={onPageSizeChange}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={onColumnVisibilityChange}
            enableColumnSelector={false}
        />
    );
}
